import { useCallback, useState } from 'react'
import type { ConsentBannerProps, ConsentCategory, ConsentLabels } from '../types.js'
import { DEFAULT_CATEGORIES, getOptionalCategories, getRequiredCategories } from '../consent-categories.js'
import { useConsent } from './use-consent.js'

const DEFAULT_LABELS: ConsentLabels = {
  title: 'We value your privacy',
  description:
    'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. You can choose which categories to allow.',
  acceptAll: 'Accept all',
  rejectAll: 'Reject all',
  manage: 'Manage preferences',
  save: 'Save preferences',
  necessary: 'Necessary',
  analytics: 'Analytics',
  marketing: 'Marketing',
  preferences: 'Preferences',
}

/**
 * GDPR-compliant cookie banner component.
 *
 * Uses Tailwind CSS classes and is compatible with shadcn/ui styling.
 * Fixed to the bottom of the screen with animated entrance.
 * Supports dark mode via Tailwind dark: classes.
 */
export function CookieBanner({
  onAcceptAll,
  onAcceptSelected,
  onRejectAll,
  labels: labelsOverride,
}: ConsentBannerProps) {
  const { showBanner, acceptAll, acceptSelected, rejectAll } = useConsent()
  const [showManage, setShowManage] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState<Set<ConsentCategory>>(() => {
    const set = new Set<ConsentCategory>()
    for (const cat of getRequiredCategories()) set.add(cat)
    for (const cat of getOptionalCategories()) set.add(cat)
    return set
  })

  const labels: ConsentLabels = { ...DEFAULT_LABELS, ...labelsOverride }

  const handleAcceptAll = useCallback(() => {
    acceptAll()
    onAcceptAll?.()
  }, [acceptAll, onAcceptAll])

  const handleRejectAll = useCallback(() => {
    rejectAll()
    onRejectAll?.()
  }, [rejectAll, onRejectAll])

  const handleSavePreferences = useCallback(() => {
    const cats = Array.from(selectedCategories)
    acceptSelected(cats)
    onAcceptSelected?.(cats)
  }, [acceptSelected, selectedCategories, onAcceptSelected])

  const toggleCategory = useCallback(
    (category: ConsentCategory) => {
      // Required categories cannot be toggled off
      if (getRequiredCategories().includes(category)) return
      setSelectedCategories((prev) => {
        const next = new Set(prev)
        if (next.has(category)) {
          next.delete(category)
        } else {
          next.add(category)
        }
        return next
      })
    },
    [],
  )

  if (!showBanner) return null

  const optionalCategories = getOptionalCategories()
  const requiredCategories = getRequiredCategories()

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 animate-slide-up"
      role="dialog"
      aria-label={labels.title}
    >
      <div className="mx-auto max-w-4xl p-4">
        <div className="rounded-lg border border-border bg-surface shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div className="p-6">
            {/* Header */}
            <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
              {labels.title}
            </h2>
            <p className="mt-2 text-sm text-text-secondary dark:text-gray-400">
              {labels.description}
            </p>

            {/* Manage preferences panel */}
            {showManage && (
              <div className="mt-4 space-y-3 rounded-md border border-border bg-background p-4 dark:border-gray-700 dark:bg-gray-800">
                {/* Required categories */}
                {requiredCategories.map((cat) => (
                  <div key={cat} className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-text-primary dark:text-gray-200">
                        {labels[cat]}
                      </span>
                      <p className="text-xs text-text-secondary dark:text-gray-500">
                        {DEFAULT_CATEGORIES[cat].description}
                      </p>
                    </div>
                    <div
                      className="inline-flex h-6 w-10 shrink-0 cursor-not-allowed items-center rounded-full bg-primary opacity-60"
                      aria-label={`${labels[cat]} (always enabled)`}
                    >
                      <span className="ml-4 h-5 w-5 rounded-full bg-white shadow-sm" />
                    </div>
                  </div>
                ))}

                {/* Optional categories */}
                {optionalCategories.map((cat) => {
                  const isOn = selectedCategories.has(cat)
                  return (
                    <div key={cat} className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-text-primary dark:text-gray-200">
                          {labels[cat]}
                        </span>
                        <p className="text-xs text-text-secondary dark:text-gray-500">
                          {DEFAULT_CATEGORIES[cat].description}
                        </p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isOn}
                        aria-label={labels[cat]}
                        onClick={() => toggleCategory(cat)}
                        className={`inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                          isOn
                            ? 'bg-primary'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${
                            isOn ? 'translate-x-4' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleAcceptAll}
                className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                {labels.acceptAll}
              </button>
              <button
                type="button"
                onClick={handleRejectAll}
                className="inline-flex items-center rounded-md border border-border bg-background px-4 py-2 text-sm font-medium text-text-primary shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                {labels.rejectAll}
              </button>
              {!showManage ? (
                <button
                  type="button"
                  onClick={() => setShowManage(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {labels.manage}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  className="inline-flex items-center rounded-md border border-primary bg-transparent px-4 py-2 text-sm font-medium text-primary shadow-sm hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                >
                  {labels.save}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
